/**
 * Budget Detail ViewModel Hook
 *
 * Single source of truth for all derived budget-detail state.
 * Calls useBudgetAnalytics once, memoizes expensive derivations,
 * and returns a flat object consumed via BudgetDetailContext.
 */

import type { BudgetAnalytics, BudgetWithProgress } from '@/shared/types';
import { parseMoney, parseMoneyToCents } from '@/shared/utils/format';
import { useMemo } from 'react';
import { useBudgetAnalytics } from '../../hooks';
import { daysBetween, daysElapsedInPeriod, daysLeftInPeriod, getProgressColor, getTextColor } from '../../utils';

export interface BudgetDetailStats {
  dailyAvg: number;
  projected: number;
  dailyLimit: number;
  totalDays: number;
  daysLeft: number;
  elapsed: number;
}

export interface BudgetDetailViewModel {
  budget: BudgetWithProgress;
  analytics: BudgetAnalytics | undefined;
  analyticsLoading: boolean;

  stats: BudgetDetailStats | null;

  spentCents: number;
  effectiveBudgetCents: number;
  remainingCents: number;
  percentUsed: number;
  progressWidth: number;
  progressColor: string;
  textColor: string;
  isOverBudget: boolean;
  isPersonal: boolean;

  chartData: Array<{ date: string; amount: number }>;
}

export function useBudgetDetailViewModel(budget: BudgetWithProgress, open: boolean): BudgetDetailViewModel {
  const { data: analytics, isLoading: analyticsLoading } = useBudgetAnalytics(
    open && budget ? budget.id : undefined
  );

  const stats = useMemo<BudgetDetailStats | null>(() => {
    if (!budget) return null;

    const totalSpent = parseMoney(budget.spent);
    const effectiveBudget = parseMoney(budget.effective_budget);
    const totalDays = daysBetween(budget.current_period_start, budget.current_period_end);
    const elapsed = daysElapsedInPeriod(budget.current_period_start);
    const daysLeft = daysLeftInPeriod(budget.current_period_end);

    const dailyAvg = elapsed > 0 ? totalSpent / elapsed : 0;
    const projected = dailyAvg * totalDays;
    const dailyLimit = totalDays > 0 ? effectiveBudget / totalDays : 0;

    return { dailyAvg, projected, dailyLimit, totalDays, daysLeft, elapsed };
  }, [budget]);

  const chartData = useMemo(() => {
    if (!analytics?.daily_spending?.length) return [];
    return analytics.daily_spending.map((d) => ({
      date: d.date,
      amount: parseMoney(d.amount),
    }));
  }, [analytics]);

  const percentUsed = budget.percent_used;
  const notifyAtPercent = budget.notify_at_percent;

  return useMemo<BudgetDetailViewModel>(
    () => ({
      budget,
      analytics,
      analyticsLoading,
      stats,
      spentCents: parseMoneyToCents(budget.spent),
      effectiveBudgetCents: parseMoneyToCents(budget.effective_budget),
      remainingCents: parseMoneyToCents(budget.remaining),
      percentUsed,
      progressWidth: Math.min(percentUsed, 100),
      progressColor: getProgressColor(percentUsed, notifyAtPercent),
      textColor: getTextColor(percentUsed, notifyAtPercent),
      isOverBudget: budget.is_over_budget,
      isPersonal: !!budget.user_id,
      chartData,
    }),
    [budget, analytics, analyticsLoading, stats, chartData, percentUsed, notifyAtPercent]
  );
}
